import React, { forwardRef, useImperativeHandle, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Grid, TextField } from '@material-ui/core'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import { fsExcludeClass } from '../../../../../utils/fsExclude'
import { useAbundanceEngineBranding } from '../../../../../api/abundanceEngine'
import { useFeatureFlag } from '../../../../../redux/slices/appConfig'
import { FEATURE_FLAG } from '../../../../../constants'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import Loader from '../../Models/Loader'

const useStyles = makeStyles((theme) => ({
  userInfoForm: {
    maxWidth: '800px',
    display: 'flex',
    justifyContent: 'center',
    padding: '10px',
    '& p': {
      color: 'var(--color-primary-600)'
    }
  },
  inputLabel: {
    color: 'var(--text-color-main)',
    fontWeight: 600,
    '& span': {
      color: 'var(--text-color-main)'
    }
  }
}))

const useFormValues = (wo) => {
  const defaultValues = useMemo(() => {
    return {
      userId: wo.userId,
      externalUserId: wo.externalUserId,
      firstName: wo?.firstName ?? '',
      lastName: wo?.lastName ?? '',
      brandingConfigKey: wo?.brandingConfigKey || ''
    }
  }, [wo])

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues
  })

  return formMethods
}

const WealthOwnerInfoForm = forwardRef(function WealthOwnerInfoForm ({ wealthOwner }, ref) {
  const classes = useStyles()
  const { handleSubmit, formState, ...formValues } = useFormValues(wealthOwner)
  const labelProps = {
    className: classes.inputLabel
  }
  const { data: brandingOptions, isLoading: isLoadingBranding } = useAbundanceEngineBranding()
  const brandingSelectOptions = useMemo(() => {
    if (!brandingOptions) return []
    return brandingOptions.map(option => ({
      value: option.brandKey,
      label: option.brandName || option.brandKey
    }))
  }, [brandingOptions])

  const { active: multiBrand } = useFeatureFlag(FEATURE_FLAG.MULTI_BRAND_FIRM)

  useImperativeHandle(ref, () => ({
    handleSubmit
  }), [handleSubmit])

  if (isLoadingBranding && multiBrand) {
    return <Loader />
  }
  return (
    <FormProvider handleSubmit={handleSubmit} formState={formState} {...formValues}>
      <FadeIn className={classes.userInfoForm}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Controller
              name='firstName'
              rules={{ required: true, maxLength: 30 }}
              render={({ field, fieldState }) => (
                <TextField
                  className={fsExcludeClass}
                  label='First Name'
                  fullWidth
                  helperText='Required, 30 max characters'
                  error={(formState.isSubmitted || fieldState.isDirty || fieldState.isTouched) && fieldState.error}
                  inputProps={field}
                  InputLabelProps={labelProps}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Controller
              name='lastName'
              rules={{ required: true, maxLength: 30 }}
              render={({ field, fieldState }) => (
                <TextField
                  className={fsExcludeClass}
                  label='Last Name'
                  fullWidth
                  helperText='Required, 30 max characters'
                  error={(formState.isSubmitted || fieldState.isDirty || fieldState.isTouched) && fieldState.error}
                  inputProps={field}
                  InputLabelProps={labelProps}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              className={fsExcludeClass}
              label='Email'
              fullWidth
              disabled
              value={wealthOwner.email}
              InputProps={{ disableUnderline: true }}
              InputLabelProps={labelProps}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              label='External User Id'
              fullWidth
              disabled
              value={wealthOwner.externalUserId}
              InputProps={{ disableUnderline: true }}
              InputLabelProps={labelProps}
            />
          </Grid>
          {multiBrand ? (
            <Grid item xs={12} md={12}>
              <Controller
                name='brandingConfigKey'
                rules={{ required: { value: true, message: 'Required' } }}
                render={({ field, fieldState }) => (
                  <SydLabel
                    label='Branding'
                    required description='Required'
                    error={fieldState.error}
                  >
                    <SydSelect
                      {...field}
                      options={brandingSelectOptions}
                      placeholder='Select Branding'
                      size='lg'
                    />
                  </SydLabel>
                )}
              />
            </Grid>
          ) : null}
        </Grid>
      </FadeIn>
    </FormProvider>
  )
})

WealthOwnerInfoForm.propTypes = {
  wealthOwner: PropTypes.shape({
    userId: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    externalUserId: PropTypes.string,
    brandingConfigKey: PropTypes.string
  })
}

export default WealthOwnerInfoForm
